import { useCallback, useMemo } from "react";
import { useMutation } from "@apollo/client/react";
import {
  AppealSupportingDocument as SharedAppealSupportingDocument,
  AppealSupportingDocumentType as SharedAppealSupportingDocumentType,
  ButtonSlotProps,
  DocumentUploadModal as SharedDocumentUploadModal,
  DocumentUploadModalConfig,
  DocumentUploadModalDeps,
  DocumentUploadModalMode,
} from "shared/src/components/document-upload-modal";
import { UploadFileInput } from "shared/src/components/forms/upload-file-input";
import { getClientSafeErrorMessage } from "shared/src/utils/apollo-error-utils";
import {
  AppealSupportingDocument,
  AppealSupportingDocumentType,
} from "@/__generated__/graphql.ts";
import { Button } from "@/components/buttons/button";
import { downloadFile, uploadFileToUrl } from "@/utils/file-requests.ts";
import { getFileUrlsForFile } from "@/utils/file-conversion.ts";
import {
  DELETE_APPEAL_SUPPORTING_DOCUMENT,
  GENERATE_SUPPORTING_DOCUMENT_UPLOAD_URLS,
  GET_SUPPORTING_DOCUMENTS_FOR_APPEAL,
  UPSERT_APPEAL_SUPPORTING_DOCUMENT,
} from "../../graphql/requests";

type ProviderAppealSupportingDocument = Omit<
  AppealSupportingDocument,
  "isIncludedInAppeal"
>;

const EXCLUDED_DOCUMENT_TYPES: SharedAppealSupportingDocumentType[] = [
  SharedAppealSupportingDocumentType.CoverLetter,
  SharedAppealSupportingDocumentType.InsuranceForm,
  SharedAppealSupportingDocumentType.PatientConsent,
];

const ADDITIONAL_MODES: DocumentUploadModalConfig["additionalModes"] = [
  {
    mode: DocumentUploadModalMode.ADD_LAB_RESULTS,
    documentType: SharedAppealSupportingDocumentType.LabResults,
  },
];

const ButtonSlot = ({
  variant,
  size,
  onClick,
  disabled,
  loading,
  isFullWidth,
  tone,
  type,
  children,
}: ButtonSlotProps) => (
  <Button
    variant={variant}
    size={size}
    onClick={onClick}
    disabled={disabled}
    loading={loading}
    isFullWidth={isFullWidth}
    tone={tone}
    type={type}
  >
    {children}
  </Button>
);

type DocumentUploadModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmitDocument: () => void;
  mode: DocumentUploadModalMode;
  document: ProviderAppealSupportingDocument | null;
  onLoadingDocumentFilesError: () => void;
  appealId: string;
};

const adaptDocumentForShared = (
  document: ProviderAppealSupportingDocument | null,
): SharedAppealSupportingDocument | null => {
  if (!document) {
    return null;
  }

  return {
    documentId: document.documentId,
    documentType:
      document.documentType as unknown as SharedAppealSupportingDocumentType,
    otherDocumentTypeName: document.otherDocumentTypeName,
    files: document.files.map((file) => ({
      fileId: file.fileId,
      fileName: file.fileName,
      fileType:
        file.fileType as unknown as SharedAppealSupportingDocument["files"][number]["fileType"],
      url: file.url,
      pageCount: file.pageCount,
    })),
  };
};

export const DocumentUploadModal = ({
  isOpen,
  onClose,
  onSubmitDocument,
  mode,
  document,
  onLoadingDocumentFilesError,
  appealId,
}: DocumentUploadModalProps) => {
  const [generateUploadUrlsMutation] = useMutation(
    GENERATE_SUPPORTING_DOCUMENT_UPLOAD_URLS,
  );

  const [upsertAppealSupportingDocument] = useMutation(
    UPSERT_APPEAL_SUPPORTING_DOCUMENT,
    {
      refetchQueries: [
        {
          query: GET_SUPPORTING_DOCUMENTS_FOR_APPEAL,
          variables: { appealId },
        },
      ],
    },
  );

  const [deleteDocumentMutation] = useMutation(
    DELETE_APPEAL_SUPPORTING_DOCUMENT,
  );

  const generateUploadUrls = useCallback<
    DocumentUploadModalDeps["generateUploadUrls"]
  >(
    async (input) => {
      const result = await generateUploadUrlsMutation({
        variables: {
          appealId,
          document: {
            documentType:
              input.documentType as unknown as AppealSupportingDocumentType,
            otherDocumentTypeName: input.otherDocumentTypeName,
            files: input.files,
          },
        },
      });

      const data = result.data?.generateSupportingDocumentUploadUrls;

      if (!data) {
        throw new Error("Failed to generate upload URLs");
      }

      return {
        documentId: data.documentId,
        fileUploadDetails: data.fileUploadDetails.map((detail) => ({
          fileId: detail.fileId,
          fileName: detail.fileName,
          uploadUrl: detail.uploadUrl,
        })),
      };
    },
    [appealId, generateUploadUrlsMutation],
  );

  const upsertDocument = useCallback<DocumentUploadModalDeps["upsertDocument"]>(
    async (input) => {
      await upsertAppealSupportingDocument({
        variables: {
          appealId,
          document: {
            documentId: input.documentId,
            documentType:
              input.documentType as unknown as AppealSupportingDocumentType,
            otherDocumentTypeName: input.otherDocumentTypeName,
            files: input.files,
          },
        },
      });
    },
    [appealId, upsertAppealSupportingDocument],
  );

  const deleteDocument = useCallback<DocumentUploadModalDeps["deleteDocument"]>(
    async (documentId) => {
      await deleteDocumentMutation({ variables: { documentId } });
    },
    [deleteDocumentMutation],
  );

  const deps = useMemo<DocumentUploadModalDeps>(
    () => ({
      getFileUrlsForFile,
      downloadFile,
      uploadFileToUrl,
      generateUploadUrls,
      upsertDocument,
      deleteDocument,
      getClientSafeErrorMessage,
    }),
    [generateUploadUrls, upsertDocument, deleteDocument],
  );

  const config = useMemo<DocumentUploadModalConfig>(
    () => ({
      excludedDocumentTypes: EXCLUDED_DOCUMENT_TYPES,
      additionalModes: ADDITIONAL_MODES,
      documentTypeDescriptions: {},
    }),
    [],
  );

  const slots = useMemo(() => ({ Button: ButtonSlot, UploadFileInput }), []);

  return (
    <SharedDocumentUploadModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmitDocument={onSubmitDocument}
      mode={mode}
      document={adaptDocumentForShared(document)}
      onLoadingDocumentFilesError={onLoadingDocumentFilesError}
      appealId={appealId}
      config={config}
      deps={deps}
      slots={slots}
    />
  );
};
